export default async function createDraftOrder(formData, paymentMethod, countryCodeValue) {
	const WELLVITA_URL = 'https://wellvita-api.netlify.app/.netlify/functions';
	const URL = `${WELLVITA_URL}/createDraftOrder?store=${Theme.settings.store_handle}`;
	const customTags = createCustomTagsForOrder(formData);
	const deliveryLineItem = getActiveDeliveryLineItem();
	const cartToken = await getCartToken();

	let cartTokenValue = "";
	if(cartToken){
		if(cartToken.includes("?key=")){
			cartTokenValue = cartToken.split("?key=")[0];
		}
	}
	const payload = createPayloadForOrder(formData, deliveryLineItem, paymentMethod, countryCodeValue, customTags, cartTokenValue);
	try {
	const response = await fetch(URL, {
		method: "POST",
		headers: {
		"Content-Type": "application/json"
		},
		body: JSON.stringify(payload)
	});
	const data = await response.json();
	if (!response.ok) {
		handleDraftOrderError(data);
		return;
	}
	return data;
	} catch (error) {
		return error;
	}

}

function createCustomTagsForOrder(formData){
	let customTags = [];
	if (window.checkout.isFreeDeliveryApplied && window.checkout.manualDiscountCode && window.checkout.totalPrice === 0 && window.checkout.giftCardAmount > 0 && window.checkout.giftCardCode){
		customTags.push("order-paid-with-gift-card-and-free-del");
	}
	else if (window.checkout.giftCardAmount > 0 && window.checkout.giftCardCode){
		customTags.push("paid-with-gift-card");
	}
	else if (window.checkout.isFreeDeliveryApplied && window.checkout.manualDiscountCode && window.checkout.totalPrice === 0){
		customTags.push("order-paid-with-free-delivery");
	}

	if(window.checkout.manualDiscountCode && window.checkout.discountCodeAmount){
		customTags.push("discount-code", "discount-code_" + window.checkout.manualDiscountCode, "discount-code-amount_" + window.checkout.discountCodeAmount);
	}
	if(window.checkout.giftCardCode && window.checkout.giftCardAmount){
		customTags.push("gift-card", "gift-card_" + window.checkout.giftCardCode, "gift-card-amount_" + window.checkout.giftCardAmount);
	}
	if(window.checkout.mediaTarget){
		customTags.push("media_target::" + window.checkout.mediaTarget);
	}
	// Intro-price flag for the ERP (Navision): general tag + per-variant for mapping.
	const introLines = document.querySelectorAll('.checkout-order__card[data-is-main="true"] .cart-line__item[data-item-intro="true"]');
	if(introLines.length){
		customTags.push("intro-price");
		introLines.forEach(line => {
			const variantId = line.dataset.itemVariantId;
			if(variantId) customTags.push("intro-price_" + variantId);
		});
	}
	// Automatic discounts (for Navision reconciliation). "_" is our field separator and "," is
	// Shopify's tag separator, so sanitize any value that may contain those.
	const sanitizeTag = (value) => String(value == null ? '' : value).replace(/[,_]/g, '-').trim();
	// Shopify rejects tags longer than 40 chars; cap at 37 (safety margin) and truncate the rest.
	const capTag = (value) => String(value == null ? '' : value).slice(0, 37);

	// Product automatic discounts: TWO short tags per discounted line, paired by variant id:
	//   <variantId>_<discountTitle>   and   <variantId>_<amount>   (amount = whole-line discount, kr float)
	// appliedDiscount.amount is PER UNIT (draft-order semantics), so multiply by quantity to
	// keep the tag whole-line for Navision reconciliation.
	const lineItems = (formData && formData.lineItems) || [];
	lineItems.forEach((lineItem) => {
		const discount = lineItem && lineItem.appliedDiscount;
		if(!discount || !discount.title) return;
		const variantId = sanitizeTag(lineItem.variantId);
		const quantity = parseInt(lineItem.quantity) || 1;
		const amount = ((parseFloat(discount.amount) || 0) * quantity).toFixed(2);
		customTags.push(
			"automatic-discount",
			`AD:${variantId}_${sanitizeTag(discount.title)}`,
			`AD:${variantId}_${amount}`
		);
	});

	// Free-shipping automatic discount: a shipping discount, tied to the variant that triggered it
	// (the line carrying data-item-fs-amount > 0). Same two-tag split.
	if(window.checkout.automaticDiscountCode && window.checkout.freeShippingDiscountAmount){
		const fsAmount = ((parseFloat(window.checkout.freeShippingDiscountAmount) || 0) / 100).toFixed(2);
		const fsLine = Array.from(document.querySelectorAll('.checkout-order__card[data-is-main="true"] .cart-line__item'))
			.find((el) => parseFloat(el.dataset.itemFsAmount) > 0);
		const fsVariantId = sanitizeTag(fsLine && fsLine.dataset ? fsLine.dataset.itemVariantId : '');
		customTags.push(
			"automatic-shipping-discount",
			`AD:${fsVariantId}_${sanitizeTag(window.checkout.automaticDiscountCode)}`,
			`AD:${fsVariantId}_${fsAmount}`
		);
	}

	// Strict: never emit a tag longer than 37 characters (covers these and the existing tags above).
	return customTags.map(capTag);
}

function handleDraftOrderError(errorData) {
	console.error("❌ Draft order failed:", errorData);

	const base = errorData?.error || 'Order creation failed.';
	const details = Array.isArray(errorData?.details)
		? errorData.details
			.map(d => d?.message ? `${[].concat(d.field || []).join(', ')}: ${d.message}` : null)
			.filter(Boolean)
			.join('\n')
		: '';

	alert(details ? `${base}\n\n${details}` : base);
}

function createPayloadDevelopmentStore(formData, deliveryLineItem, paymentMethod, countryCodeValue, customTags) {
	const initialTags = ["custom-checkout", paymentMethod || 'payment-method-not-specified'];
	const tagsCombined = [...initialTags, ...customTags];
	const country = determineCountry(countryCodeValue);
	const payload = {
		email: formData?.['checkout-email'],
		tags: tagsCombined,
		phone: formData?.['checkout-phone'],
		shippingAddress: {
			firstName: formData?.['checkout-fname'],
			lastName: formData?.['checkout-lname'],
			address1: formData?.['checkout-address'],
			city: formData?.['checkout-city'],
			country: formData?.['checkout-country'],
			zip: formData?.['checkout-postcode'],
			phone: formData?.['checkout-phone']
		},
		shippingLine: {
			title: deliveryLineItem?.deliveryMethod,
			price: deliveryLineItem?.deliveryPrice
		},
		lineItems: [
			{
			  variantId: "gid://shopify/ProductVariant/47640403378470",
			  originalUnitPrice: "35.00",
			  quantity: 1
			}
		  ]
	  }; 
	  return payload;
}

function createPayloadForOrder(formData, deliveryLineItem, paymentMethod, countryCodeValue, customTags, cartToken) {
	const initialTags = ["custom-checkout", paymentMethod || 'payment-method-not-specified'];
	// Safety net: Shopify rejects any tag over 40 chars, so hard-cap every tag at 37.
	const tagsCombined = [...initialTags, ...customTags].map((tag) => String(tag == null ? '' : tag).slice(0, 37));
	const country = determineCountry(countryCodeValue);
	const payload = {
		email: formData?.['checkout-email'],
		tags: tagsCombined,
		phone: formData?.['checkout-phone'],
		shippingAddress: {
			firstName: formData?.['checkout-fname'],
			lastName: formData?.['checkout-lname'],
			address1: formData?.['checkout-address'],
			city: formData?.['checkout-city'],
			country: country,
			zip: formData?.['checkout-postcode'],
			phone: formData?.['checkout-phone']
		},
		shippingLine: {
			title: deliveryLineItem?.deliveryMethod || "",
			price: deliveryLineItem?.deliveryPrice || 0
		},
		lineItems: formData?.['lineItems'].map(item => ({
			...item,
			variantId: `gid://shopify/ProductVariant/${item.variantId}`,
		})),
		appliedDiscount: formData?.['appliedDiscount'] || null,
		note: cartToken ? `${cartToken}` : ""
	};
	return payload;
}

function getActiveDeliveryLineItem() {
	const globalWindowShipping = window.checkout;

	if(window.checkout?.isFreeDeliveryApplied){
		return {
			deliveryMethod: "Free shipping (from discount code)",
			deliveryPrice: 0
		}
	}
	if(globalWindowShipping) {
		// deliveryPrice is the NET shipping (may be 0 when the free-shipping discount fully covers
		// it) — check deliveryMethod presence, not truthiness of the price, so 0 is sent as free.
		if(globalWindowShipping.deliveryMethod){
			const originalPrice = globalWindowShipping.deliveryPrice || 0;
			const dividedPrice = parseFloat(originalPrice) / 100 || 0;
			return {
				deliveryMethod: globalWindowShipping.deliveryMethod,
				deliveryPrice: dividedPrice
			};
		}
	}
	const deliveryLineItem = document.querySelector('input[name="delivery-method"]:checked');
	if (!deliveryLineItem) {
		return null;
	}
	const dataPrice = deliveryLineItem.getAttribute('data-delivery-price');
	let dividedDataPrice = 0;
	if(dataPrice) {
		dividedDataPrice = parseFloat(dataPrice) / 100 || 0;
	}
	return {
		deliveryMethod: deliveryLineItem.getAttribute('data-full-title') || '',
		deliveryPrice: dividedDataPrice || 0
	}
}


async function getCartToken() {
	try {
		const response = await fetch(window.Shopify.routes.root + 'cart.js');
		if (!response.ok) {
			console.warn("Could not fetch cart token");
			return null;
		}
		const cartData = await response.json();
		return cartData.token || null;
	} catch (error) {
		console.warn("Error fetching cart token:", error);
		return null;
	}
}

function determineCountry(countryCodeValue){
	switch (countryCodeValue) {
		case 'dk':
		case 'DK':
			return 'Denmark';
		case 'fi':
		case 'FI':
			return 'Finland';
		case 'no':
		case 'NO':
			return 'Norway';
		case 'se':
		case 'SE':
			return 'Sweden';
		case 'nl':
		case 'NL':
			return 'Netherlands';
		case 'be':
		case 'BE':
			return 'Belgium';
		default:
			return 'Denmark';
	}
}